/**
 * Authentication against the platform-services user service.
 *
 * Framework free on purpose: the React context in AuthContext.js wraps this, and the API
 * call sites import `requireToken` directly without needing a hook.
 */

//  Per tab, like the MSAL cacheLocation this replaces, so closing the tab still signs you
//  out. Deliberately NOT a cookie: ppi-client's `tkn` cookie is non-httpOnly (so it is
//  JS-readable exactly like sessionStorage, no security gain) and carries a ~2000 day
//  expiry for a token that lives hours. sessionStorage is XSS-readable; the mitigations are
//  the short token lifetime, the absence of a refresh token, and the fact that the API
//  enforces roles regardless of what this app believes.
const TOKEN_KEY = "towpay.mm.token";

/**
 * Thrown by `requireToken` when the session has expired. The page is already navigating to
 * the login screen by the time this surfaces, so callers should re-throw it rather than
 * rendering "failed to load".
 */
export class SessionExpiredError extends Error {
  constructor(message = "Session expired") {
    super(message);
    this.name = "SessionExpiredError";
  }
}

/**
 * Decodes a JWT payload.
 *
 * Hand-rolled rather than pulling in jwt-decode: v4 is ESM only and CRA 5 does not
 * transform node_modules, so it would need a transformIgnorePatterns override, which on
 * React 17 / CRA 5 means react-app-rewired or ejecting.
 *
 * @param {string} token
 * @returns {object|null} the decoded payload, or null if it cannot be read
 */
export function decodeToken(token) {
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    return JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
    );
  } catch (error) {
    console.error("Could not decode the access token:", error);
    return null;
  }
}

/**
 * @param {string} token
 * @returns {boolean} true when the token is missing, unreadable, or past its `exp`
 */
export function isTokenExpired(token) {
  const claims = decodeToken(token);
  if (!claims || !claims.exp) return true;

  return claims.exp < Date.now() / 1000;
}

/** @returns {string|null} the stored token, or null */
export function getToken() {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch (error) {
    //  Private browsing and blocked site data both make this throw.
    console.error("Could not read the stored session:", error);
    return null;
  }
}

/** @param {string} token */
export function setToken(token) {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error("Could not store the session:", error);
  }
}

export function clearToken() {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error("Could not clear the session:", error);
  }
}

/** @returns {boolean} whether there is a usable, unexpired session */
export function isAuthenticated() {
  const token = getToken();
  return !!token && !isTokenExpired(token);
}

/** @returns {Array<number>} the platform-services role ids on the current token */
export function getRoles() {
  const claims = decodeToken(getToken());
  return (claims && Array.isArray(claims.roles) && claims.roles) || [];
}

/** @returns {string} the signed-in user's email, or an empty string */
export function getEmail() {
  const claims = decodeToken(getToken());
  return (claims && claims.email) || "";
}

/** @returns {number|null} the signed-in user's platform-services user id */
export function getUserId() {
  const claims = decodeToken(getToken());
  return (claims && claims.id) || null;
}

/**
 * Sends the browser to the login screen.
 *
 * A full page navigation rather than react-router, because `requireToken` is called from
 * plain functions that have no router context.
 *
 * @param {string} reason passed through as ?reason= so the login page can explain itself
 */
export function redirectToLogin(reason) {
  const from = `${window.location.pathname}${window.location.search}`;
  const query = new URLSearchParams();
  if (reason) query.set("reason", reason);
  if (from && from !== "/login") query.set("from", from);

  window.location.assign(`/login${query.toString() ? `?${query}` : ""}`);
}

/**
 * The token to put on an API call, or a redirect to the login screen.
 *
 * Every API call site goes through this, which is what guarantees no request ever leaves
 * with a dead token. There is no refresh token in platform-services, so an expired session
 * can only be resolved by logging in again.
 *
 * @throws {SessionExpiredError} when the session has expired
 * @returns {string} the access token
 */
export function requireToken() {
  const token = getToken();

  if (!token || isTokenExpired(token)) {
    clearToken();
    redirectToLogin("expired");
    throw new SessionExpiredError();
  }

  return token;
}

/**
 * Signs in against the platform-services user service.
 *
 * @param {string} userBaseUrl base URL of the user service
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{token: string, claims: object}>}
 * @throws {Error} with `status` set, and a message safe to show the user
 */
export async function login(userBaseUrl, email, password) {
  let response;

  try {
    response = await fetch(`${userBaseUrl}/user/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      //  clientOS drives the session timeout the user service picks.
      body: JSON.stringify({ email, password, clientOS: "web" }),
    });
  } catch (error) {
    console.error("Could not reach the login service:", error);
    const unreachable = new Error(
      "The login service is not reachable. Try again in a moment."
    );
    unreachable.status = 0;
    throw unreachable;
  }

  if (!response.ok) {
    let serverMessage = "";
    try {
      const body = await response.json();
      //  Only the message. The user service also returns file, line and function from
      //  libs.SendHttpErrorResponse, which would leak monorepo source paths to the browser.
      serverMessage = (body && body.message) || "";
    } catch {
      // No JSON body. Fall through to the status-based message.
    }

    const error = new Error(messageFor(response.status, serverMessage));
    error.status = response.status;
    throw error;
  }

  const body = await response.json();
  const token = body && body.user && body.user.jwtToken;

  if (!token) {
    throw new Error("The login service did not return a session. Contact Autura support.");
  }

  return { token, claims: decodeToken(token) };
}

/**
 * Best-effort sign out.
 *
 * The server call only blanks the stored token; the JWT itself stays valid until it
 * expires, so clearing it locally is what actually ends the session here.
 *
 * @param {string} userBaseUrl base URL of the user service
 */
export async function logout(userBaseUrl) {
  const token = getToken();
  const userId = getUserId();

  clearToken();

  if (!token || !userId || !userBaseUrl) return;

  try {
    await fetch(`${userBaseUrl}/user/${userId}/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    //  Nothing useful to do; the local session is already gone.
    console.error("Logout call failed:", error);
  }
}

function messageFor(status, serverMessage) {
  //  "Account Not Active" fires before the password is checked, so it is an enumeration
  //  oracle in its own right, but it is the one failure a user can act on and hiding it
  //  just generates support tickets.
  if (serverMessage && serverMessage.toLowerCase().includes("not active")) {
    return "This account is not active. Contact Autura support.";
  }

  //  404 (unknown email) and 401 (wrong password) are collapsed on purpose. The user
  //  service distinguishes them, which tells an attacker which addresses exist; there is no
  //  reason to amplify that here.
  if (status === 401 || status === 404 || status === 400) {
    return "We could not sign you in. Check your email and password.";
  }

  return "The login service is not reachable. Try again in a moment.";
}
